import { CurationItem } from "../../types";
import { callCurationProxy } from "../backend/backendClient";
import { getRequiredPublisherId } from "./helpers";

/**
 * Options for creating a menu item
 * @property defaultMetadata - Default metadata for the menu item
 * @property enMetadata - English metadata for the menu item
 * @property titleOverride - Override the title of the menu item
 * @property imagePackId - ID of the image pack to associate with the menu item
 * @property index - Index of the menu item in the parent list
 * @typedef {Object} CreateMenuItemOptions
 * @property {Record<string, unknown>} [defaultMetadata] - Default metadata for the menu item
 * @property {Record<string, unknown>} [enMetadata] - English metadata for the menu item
 * @property {string} [titleOverride] - Override the title of the menu item
 * @property {string} [imagePackId] - ID of the image pack to associate with the menu item
 * @property {number} [index] - Index of the menu item in the parent list
 * @returns {Promise<CurationItem>}
 */
export type CreateMenuItemOptions = {
    defaultMetadata?: Record<string, unknown>;
    enMetadata?: Record<string, unknown>;
    titleOverride?: string;
    imagePackId?: string;
    index?: number;
};

/**
 * Create a menu item
 * @param parentId
 * @param title
 * @param position
 * @param options
 * @returns {Promise<CurationItem>}
 */
export const createMenuItem = async (
    parentId: string,
    title: string,
    position: string,
    options?: CreateMenuItemOptions,
): Promise<CurationItem> => {
    if (!parentId || !title) {
        throw new Error("createMenuItem requires a parentId and title.");
    }

    const publisherId = getRequiredPublisherId();

    const finalTitle = options?.titleOverride ?? title;
    const index = options?.index ?? 0;

    const defaultMetadata = {
        "publisher-id": publisherId,
        ...(options?.defaultMetadata ?? {}),
    };

    const enMetadata = {
        title: finalTitle,
        url: position,
        ...(options?.enMetadata ?? {}),
    };

    const body = [
        {
            contentType: "menu_item",
            parentId,
            title: finalTitle,
            metadata: {
                default: defaultMetadata,
                en_US: enMetadata,
            },
            ...(options?.imagePackId ? { imagePackId: options.imagePackId } : {}),
        },
    ];

    const res = await callCurationProxy({
        path: `/list/?index=${index}&copyImagePack=true`,
        method: "POST",
        body,
    });

    const responseText = await res.text();
    if (!res.ok) {
        throw new Error(`POST Error for MenuItem: ${res.status}, ${responseText}`);
    }

    const data = JSON.parse(responseText) as { result?: CurationItem[] };
    const newMenuItem = data.result?.[0];

    if (!newMenuItem?.id) {
        throw new Error(
            `API Error: MenuItem created but no ID was returned. Response: ${responseText}`,
        );
    }

    return newMenuItem;
};
